import type React from "react";
import { useMemo, useState } from "react";
import { CheckCheck, Loader2 } from "lucide-react";
import { MageXPatient } from "@prisma/client";
import { Plural, Trans, useLingui } from "@lingui/macro";
import { Button } from "@app/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";
import { MageXPatientWithFeedbacks } from "@api/src/types/patient";
import API from "@app/services/api";
import { PatientSchema } from "@app/types/few-schot-schema";
import {
  buildFieldValidation,
  isFieldValueEmpty,
  resolveAggregatedValue,
  type FieldValidationBody,
} from "@app/utils/resolve-field-value";
import { useCanValidate } from "@app/utils/use-can-validate";

/**
 * How the page answers the bar's two questions: has a clinician ruled on this field, and which
 * day does a confirmation describe.
 *
 * Every clinical value comes from the dated projection, so ``patient.Feedbacks`` is deliberately
 * emptied — overlaying it would freeze the fields and the slider would do nothing. The answers are
 * exactly the ones the per-field check button uses: the gesture elected at the cursor's date, and
 * the fact's clinical date.
 */
export interface FieldGestureResolver {
  /** `true` when no clinician has ruled on this field yet, on the caller's own terms. */
  isPending: (fieldKey: string) => boolean;
  /** The clinical day a confirmation of this field describes. `null` = the current state. */
  effectiveDateOf: (fieldKey: string) => string | null;
  /**
   * The ACT a confirmation of this field is about, when the surface names one.
   *
   * Only an episode's sweep does. A date alone cannot place a confirmation against an episode
   * boundary — the boundaries are intervals — so a sweep that dated forty fields on the day of an
   * operation without saying which operation would hand the resolver an ambiguity the screen had
   * already resolved. Absent, and the anchor answers alone, as on the profile.
   */
  scopeDocIdOf?: (fieldKey: string) => string | null;
}

interface ValidateRemainingCardProps {
  patient: MageXPatientWithFeedbacks;
  schema: PatientSchema;
  /** Ordered keys of the fields actually rendered on the page, hidden ones excluded. */
  fieldKeys: string[];
  loadPatient: () => void;
  /** Has this dossier been read? The bar stays hidden until it has. */
  processed: boolean;
  /** See ``FieldGestureResolver``. */
  gestures: FieldGestureResolver;
  /**
   * One line naming what the sweep leaves out: a field an episode states cannot be confirmed by a
   * gesture that shares one timestamp with forty others, and a button promising "the remaining
   * fields" that leaves visible check marks behind has to say why.
   */
  scopeHint?: React.ReactNode;
  /**
   * Where the bar is standing.
   *
   * `card` closes a RECORD and is the record's last section. `inline` closes ONE EPISODE and lives
   * inside its block, under the fields it sweeps — a second card there would read as a second
   * record. Nothing else differs: same payload, same endpoint, same dialog.
   */
  variant?: "card" | "inline";
  /** What the button promises to sweep, when "the remaining fields" is not precise enough. */
  actionLabel?: React.ReactNode;
}

/**
 * Close a record in one gesture: correct what is wrong, confirm the rest.
 *
 * Reviewing a record is mostly agreeing with it — production counts 6 911 validations
 * against 4 575 corrections, and 30 gestures per record-day at the median. This bar pays
 * for the agreeing half once instead of once per field.
 *
 * It sweeps ONLY fields no clinician has ruled on yet, and only those the practitioner
 * can actually see: the visible list is assembled client-side from the ontology and the
 * practitioner's own hidden-field selection, so it is sent explicitly rather than
 * recomputed server-side. A field the doctor never had on screen is never validated in
 * their name.
 *
 * Empty fields are swept like the others. An absence the clinician read and accepted is
 * an answer — the same one the per-field button records as "Confirmer l'absence de
 * valeur".
 *
 * What it writes is indistinguishable from clicking each check in turn: same payload builder, same
 * endpoint.
 *
 * Same bar, two placements, because the page holds two scopes rather than one. The
 * record's profile is swept once; an EPISODE is swept from inside its own block, with
 * ``variant="inline"``. The split is not cosmetic and not a preference: one sweep shares one
 * timestamp and the log's key is (patient, field, created_at), so a field two episodes state
 * differently cannot be confirmed twice in one request. Inside ONE episode it is stated once —
 * that is the value the block draws — so an episode is exactly the largest scope this gesture can
 * close without lying about a date.
 */
export default function ValidateRemainingCard({
  patient,
  schema,
  fieldKeys,
  loadPatient,
  processed,
  gestures,
  scopeHint,
  variant = "card",
  actionLabel,
}: ValidateRemainingCardProps) {
  const canValidate = useCanValidate();
  const { t } = useLingui();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  const pending = useMemo(() => {
    const bodies: FieldValidationBody[] = [];
    let empty = 0;
    for (const key of fieldKeys) {
      const def = schema[key];
      // Frozen fields carry no gesture at all on the field row — the bar must not
      // invent one for them.
      if (def?.frozen) continue;
      const property = key as keyof MageXPatient;
      if (!gestures.isPending(key)) continue;
      const { body } = buildFieldValidation(patient, property, def?.type);
      // Each confirmation is dated by the fact it confirms, not by the sweep.
      bodies.push({
        ...body,
        effective_date: gestures.effectiveDateOf(key),
        scope_doc_id: gestures.scopeDocIdOf?.(key) ?? null,
      });
      if (isFieldValueEmpty(resolveAggregatedValue(patient, property, def?.type)))
        empty += 1;
    }
    return { bodies, empty, filled: bodies.length - empty };
  }, [fieldKeys, patient, schema, gestures]);

  if (!canValidate || !processed) return null;
  if (pending.bodies.length === 0) return null;

  const submit = async () => {
    setSubmitting(true);
    setFailed(false);
    const response = await API.post({
      path: `/patient/${patient.id_magex}/feedback/validate-remaining`,
      body: { fields: pending.bodies },
    });
    setSubmitting(false);
    if (response.ok) {
      setConfirmOpen(false);
      loadPatient();
    } else {
      console.error("Bulk validation POST failed", response);
      setFailed(true);
    }
  };

  const count = (
    <p>
      <Plural
        value={pending.bodies.length}
        one="# champ n'a pas encore été revu"
        other="# champs n'ont pas encore été revus"
      />
    </p>
  );

  // What the bar does NOT sweep, or what it dates its confirmations from. Saying nothing would make
  // a « valider les champs restants » button that leaves check marks on screen an inexplicable
  // anomaly.
  const hint = scopeHint ? <p className="mt-1 text-xs text-slate-500">{scopeHint}</p> : null;

  const trigger = (
    <Button
      onClick={() => setConfirmOpen(true)}
      className="shrink-0"
      variant={variant === "inline" ? "outline" : "default"}
      aria-label={t`Valider tous les champs restants`}
    >
      <CheckCheck className="mr-2 h-4 w-4" />
      {actionLabel ?? <Trans>Valider les champs restants</Trans>}
    </Button>
  );

  return (
    <>
      {variant === "inline" ? (
        // Under the episode's fields, inside its block: this gesture closes THIS act and nothing of
        // the person. The separator says we are leaving the list, not the record.
        //
        // `@container` carried here: the `@sm` variants query the nearest container, and the episode
        // block declares none — without it the bar would stay stacked at every width.
        <div className="@container mt-6 border-t border-slate-200 pt-4">
          <div className="flex flex-col gap-3 @sm:flex-row @sm:items-center @sm:justify-between">
            <div className="text-sm text-slate-600">
              {count}
              {hint}
            </div>
            {trigger}
          </div>
        </div>
      ) : (
        /* Rendered as the record's last section, with the same card treatment as the
           ontology groups above: closing the record is part of reading it, not a floating
           toolbar bolted onto the page. */
        <Card className="border-l-4 border-l-primary">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <span>✅</span>
              <span>
                <Trans>Revue du dossier</Trans>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 @sm:flex-row @sm:items-center @sm:justify-between">
            <div className="text-sm text-slate-600">
              {count}
              {hint}
            </div>
            {trigger}
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={(open) => !open && setConfirmOpen(false)}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>
              {actionLabel ?? <Trans>Valider les champs restants</Trans>}
            </DialogTitle>
            <DialogDescription>
              <Trans>
                Les champs que vous avez corrigés ou déjà validés ne sont pas
                concernés.
              </Trans>
            </DialogDescription>
          </DialogHeader>

          <ul className="space-y-2 py-2 text-sm text-slate-700">
            <li className="flex items-center justify-between rounded-md bg-blue-50 px-3 py-2">
              <Trans>Valeurs extraites confirmées telles quelles</Trans>
              <span className="font-medium text-blue-900">{pending.filled}</span>
            </li>
            <li className="flex items-center justify-between rounded-md bg-blue-50 px-3 py-2">
              <Trans>Champs vides confirmés comme devant rester vides</Trans>
              <span className="font-medium text-blue-900">{pending.empty}</span>
            </li>
          </ul>

          <p className="text-xs text-slate-500">
            <Trans>
              Ces réponses seront enregistrées comme si vous les aviez validées une
              par une. Vous pourrez toujours corriger un champ ensuite.
            </Trans>
          </p>

          {failed && (
            <p className="text-sm text-red-600">
              <Trans>Erreur lors de la validation. Aucun champ n'a été validé.</Trans>
            </p>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
            >
              <Trans>Annuler</Trans>
            </Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Plural
                value={pending.bodies.length}
                one="Valider # champ"
                other="Valider # champs"
              />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
